﻿import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { Trans, useTranslation } from "react-i18next";

import { GeoMiningProductionTruckDTO } from "../../../DTOs/Geology/GeoMiningProductionTruckDTO";
import { DiggingLocationDTO } from "../../../DTOs/Production/DiggingLocationDTO";
import { LocationDTO } from "../../../DTOs/Production/LocationDTO";
import { TruckLoadDTO } from "../../../DTOs/Production/TruckLoadDTO";
import { TruckLoadsPagedResult } from "../../../DTOs/Production/TruckLoadsPagedResult";
import { detectionSourceLabel, isAutoDetected } from "../../../DTOs/Common/DetectionSource";
import { VerificationIndicator } from "../../../components/VerificationIndicator/VerificationIndicator";
import { diggingAjax } from "../../../ajax/DiggingAjax";
import { loadingsMutations } from "../../../offline/loadingsMutations";
import { geologyAjax } from "../../../ajax/GeologyAjax";
import { useGlobalModal } from "../../../components/Contexts/GlobalModalContext";
import { DataTable, TableRow } from "../../../components/DataTable/DataTable";
import { cellValue, withCellValue } from "../../../components/DataTable/cellValue";
import { DataTableFilteredRequest, useDataTable } from "../../../components/DataTable/TableHook";
import { GroupedSelect } from "../../../components/GroupedSelect/GroupedSelect";
import { useOptions, useMaterialTypeGroupOptions } from "../../../components/Hooks/useOptions";
import useShiftLockInfo from "../../../components/Hooks/useShiftLockInfo";
import { useOnlineStatus } from "../../../components/Hooks/useOnlineStatus";
import { useScrollAndPulseRow } from "../../../components/Hooks/useScrollAndPulseRow";
import { useTimezone } from "../../../components/Providers/TimezoneProvider";
import { isCurrentShift } from "../../../utilities/helpers/shiftLockStateHelpers";
import {
  isHourSlotInFuture,
  hourIndexFromClockHour,
  clockHourFromHourIndex,
  resolveLiveShiftHourIndex,
} from "../../../utilities/helpers/shiftHourHelpers";
import MathInput from "../../../components/MathInput/MathInput";
import { TripleShift } from "../../../types/Shift";
import MoveLoadingRecordModal from "./MoveLoadingRecordModal";
import SaveButton from "../../../components/Buttons/SaveButton";
import CopyPreviousHourButton from "./CopyPreviousHourButton";
import TruckLoadActions from "./TruckLoadActions";
import TrucksTableFooter from "./TrucksTableFooter";
import TrucksTableNoResultsContent from "./TrucksTableNoResultsContent";
import { truckLoadFromTableRow } from "./truckLoadFromTableRow";
import { useOfflineShiftTotal } from "./useOfflineShiftTotal";
import { shiftDurationHours, shiftStartHour } from "../../../offline/shiftBands";
import { isOnlineNow } from "../../../offline/connectivity";
import { useCanWrite, useIsReadOnly } from "../../../utilities/helpers/roleUtilities";
import { useTelemetryEnabled } from "../../../components/Hooks/useTelemetryEnabled";
import { ShowAllAutocomplete } from "../../../components/ShowAllAutocomplete/ShowAllAutocomplete";
import { TRUCK_LABELS } from "../../../components/ShowAllAutocomplete/showAllLabels";

interface InsertRowState {
  truckId: number;
  loads: number;
  loadsExpr?: string;
  materialTypeClassId: number;
  destinationId: number;
}

interface InsertRowProps {
  state: InsertRowState;
  setState: React.Dispatch<React.SetStateAction<InsertRowState>>;
  onInsert: () => Promise<boolean>;
  truckOptions: GeoMiningProductionTruckDTO[];
  isTruckOptionsLoading: boolean;
  materialTypes: { value: number; label: string; group: string; materialTypeId: number; isWaste: boolean }[];
  isMaterialTypesLoading: boolean;
  destinationOptions: LocationDTO[];
  allowedNonWasteLocationTypeIds: number[];
  allowedWasteLocationTypeIds: number[];
  isDestinationOptionsDataLoading: boolean;
  isReadOnly: boolean;
}

type TruckLoadsTableMetaData = {
  currentHourTotalLoads: number;
  shiftTotalLoads: number;
};

const InsertInputsRow = ({
  state,
  setState,
  onInsert,
  truckOptions,
  isTruckOptionsLoading,
  materialTypes,
  isMaterialTypesLoading,
  destinationOptions,
  allowedNonWasteLocationTypeIds,
  allowedWasteLocationTypeIds,
  isDestinationOptionsDataLoading,
  isReadOnly
}: InsertRowProps) => {
  const { t } = useTranslation("production");
  const [isWorking, setIsWorking] = useState(false);
  const telemetryEnabled = useTelemetryEnabled();

  const materialClassID = state.materialTypeClassId;
  const isSelectedMaterialClassWaste = materialTypes.find(m => m.value === materialClassID)?.isWaste;
  // TODO: this below should be moved to the backend to make sure we have a single source of truth
  const allowedTypeIds = isSelectedMaterialClassWaste ? allowedWasteLocationTypeIds : allowedNonWasteLocationTypeIds;
  const opts = destinationOptions.filter(d => allowedTypeIds.includes(d.locationTypeId));

  return (
    <ul className="flex gap-2 border-b border-theme-border py-1">
      <li className="basis-[18%] flex items-center justify-center text-[0.9rem] w-full">
        <ShowAllAutocomplete
          labels={TRUCK_LABELS}
          value={truckOptions.find(o => o.plantId === state.truckId) ?? null}
          classes={{ root: "w-full" }}
          options={truckOptions}
          loading={isTruckOptionsLoading}
          disabled={isWorking || isTruckOptionsLoading || isReadOnly}
          onChange={(_, opt) => setState(({ ...state, truckId: opt?.plantId ?? 0 }))}
          getOptionLabel={o => o.plantRef}
          renderInput={params => (
            <TextField
              {...params}
              placeholder={t("trucksTable.selectTruck")}
              size="small"
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  endAdornment: (
                    <>
                      {isTruckOptionsLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
                      {params.slotProps.input.endAdornment}
                    </>
                  )
                }
              }}
            />
          )}
        />
      </li>

      <li className="basis-[10%] flex items-center justify-center text-[0.9rem] w-full">
        <MathInput
          value={state.loads}
          disabled={isWorking || isReadOnly}
          onValueChange={(val) => setState({ ...state, loads: val })}
          step={1}
          className="w-full h-full"
        />
      </li>

      <li className="basis-[15%] flex items-center justify-center text-[0.9rem] w-full">
        <GroupedSelect
          options={materialTypes}
          value={state.materialTypeClassId}
          loading={isMaterialTypesLoading}
          disabled={isWorking || isMaterialTypesLoading || isReadOnly}
          onChange={e => setState({ ...state, materialTypeClassId: e ?? 0 })}
          placeholder={t("trucksTable.selectMaterial")}
        />
      </li>

      <li className="basis-[18%] flex items-center justify-center text-[0.9rem] w-full">
        <Autocomplete
          value={destinationOptions.find(o => o.id === state.destinationId) ?? null}
          classes={{ root: "w-full" }}
          options={opts}
          loading={isDestinationOptionsDataLoading}
          disabled={isWorking || isDestinationOptionsDataLoading || isReadOnly}
          getOptionLabel={(o) => o.name}
          onChange={(_, opt) => setState({ ...state, destinationId: opt?.id ?? 0 })}
          renderInput={params => (
            <TextField
              {...params}
              placeholder={t("trucksTable.selectDestination")}
              size="small"
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  endAdornment: (
                    <>
                      {isDestinationOptionsDataLoading ?
                        <FontAwesomeIcon icon={faSpinner} spin /> : null}
                      {params.slotProps.input.endAdornment}
                    </>
                  )
                }
              }}
            />
          )}
        />
      </li>

      <li className="basis-[11%] flex items-center justify-center text-[0.9rem] w-full">
        <span>{t("trucksTable.you")}</span>
      </li>

      {telemetryEnabled && <li key="source" className="basis-[8%]" />}

      <li className="basis-[20%] flex items-center justify-center text-[0.9rem] w-full">
        <button
          disabled={isWorking || isReadOnly}
          onClick={async () => {
            if (isWorking) return;
            setIsWorking(true);
            await onInsert();
            setIsWorking(false);
          }}
          className="bg-theme-info hover:bg-theme-info-hover disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed transition ease-in-out text-white font-bold py-2 px-4 rounded w-full whitespace-nowrap"
        >
          {t("actions.add")}
        </button>
      </li>
    </ul>
  );
};

interface TrucksTableProps {
  selectedDate: DateTime;
  selectedShift: TripleShift;
  selectedLocation: DiggingLocationDTO;
  availableLocations: DiggingLocationDTO[];
  destinationOptions: LocationDTO[];
  allowedNonWasteLocationTypeIds: number[];
  allowedWasteLocationTypeIds: number[];
  isDestinationOptionsDataLoading: boolean;
  registerUpdateTrucksTableRowFn: (fn: () => void) => void;
  initialHourIndex?: number | null;
  initialLoadingId?: number | null;
  onNavigationConsumed?: () => void;
}

export const TrucksTable: React.FC<TrucksTableProps> = ({
  selectedDate,
  selectedShift,
  selectedLocation,
  availableLocations,
  destinationOptions,
  allowedNonWasteLocationTypeIds,
  allowedWasteLocationTypeIds,
  isDestinationOptionsDataLoading,
  registerUpdateTrucksTableRowFn,
  initialHourIndex,
  initialLoadingId,
  onNavigationConsumed
}) => {
  const { t } = useTranslation("production");
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useGlobalModal();
  const isReadOnly = useIsReadOnly();
  const canVerify = useCanWrite("Digging.VerifyTruckLoads");
  const isOnline = useOnlineStatus();

  const [lockedTabKey, setLockedTabKey] = useState<string | null>("loads");
  const navConsumedRef = useRef(false);

  // Note: from the configured shift bands, not a DAY/NIGHT ternary. This screen hardcoded 7 and 19
  // against bands of 06:00 and 18:00, so every hour label was an hour out and the live-hour marker
  // pointed at the wrong button.
  const shiftStartClockHour = shiftStartHour(selectedShift);
  const shiftHourCount = shiftDurationHours(selectedShift);
  const indexFromClockHour = useCallback(
    (clockHour: number) => hourIndexFromClockHour(clockHour, shiftStartClockHour),
    [shiftStartClockHour]
  );
  const clockHourFromIndex = useCallback(
    (idx: number) => clockHourFromHourIndex(idx, shiftStartClockHour),
    [shiftStartClockHour]
  );

  const hours = useMemo(() => {
    const startClock = selectedLocation.startTime.toLocal().hour;
    const startIndex = indexFromClockHour(startClock);
    const indices: number[] = [];
    for (let i = startIndex; i <= shiftHourCount; i++) indices.push(i);
    return indices.map(i => ({
      index: i,
      label: `${clockHourFromIndex(i)} - ${(clockHourFromIndex(i) + 1) % 24}`
    }));
  }, [selectedLocation.startTime, indexFromClockHour, clockHourFromIndex, shiftHourCount]);

  const { shiftLockState } = useShiftLockInfo(selectedDate, selectedShift);
  const { timezone } = useTimezone();
  const isLatestShift = useMemo(
    () => isCurrentShift(selectedDate, selectedShift === "NIGHT" ? "N" : "D", shiftLockState),
    [selectedDate, selectedShift, shiftLockState]
  );

  const [nowTick, setNowTick] = useState(() => DateTime.now());
  useEffect(() => {
    if (!isLatestShift) return;
    const id = setInterval(() => setNowTick(DateTime.now()), 30_000);
    return () => clearInterval(id);
  }, [isLatestShift]);

  const liveHourIndex = useMemo(
    () => resolveLiveShiftHourIndex(
      nowTick.setZone(timezone ?? "UTC").hour, shiftStartClockHour, shiftHourCount, isLatestShift),
    [nowTick, timezone, shiftStartClockHour, shiftHourCount, isLatestShift]
  );

  const [selectedHourIndex, setSelectedHourIndex] = useState<number>(() => {
    if (initialHourIndex != null) return Math.min(Math.max(initialHourIndex, 1), shiftHourCount);
    const start = indexFromClockHour(selectedLocation.startTime.toLocal().hour);
    return Math.min(Math.max(start, 1), shiftHourCount);
  });

  const telemetryEnabled = useTelemetryEnabled();
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);

  const { options: materialTypes, isLoading: isMaterialTypesLoading } = useMaterialTypeGroupOptions();
  const { options: truckOptionsRaw, isLoading: isTruckOptionsLoading } = useOptions<GeoMiningProductionTruckDTO, GeoMiningProductionTruckDTO>(
    ['geology', 'productionTrucks', selectedDate.toISODate()],
    () => geologyAjax.getProductionTruckingTrucks(selectedDate),
    x => x,
  );

  const truckOptions = useMemo(
    () => [...truckOptionsRaw].sort((a, b) => a.plantRef.localeCompare(b.plantRef, undefined, { numeric: true, sensitivity: 'base' })),
    [truckOptionsRaw]
  );

  const handleMoveLoadingRecord = useCallback(
    async (loadingId: number, newDiggingPatternId: number) => {
      const response = await diggingAjax.moveLoadingRecord(loadingId, newDiggingPatternId);

      if (!response.success) {
        response.errors?.forEach((error, index) =>
          enqueueSnackbar(error.description, { variant: "error", autoHideDuration: 5000 + index * 1500 })
        );
        return false;
      }

      return true;
    },
    [enqueueSnackbar]
  );

  const [insertState, setInsertState] = useState<InsertRowState>({
    truckId: 0,
    loads: 0,
    materialTypeClassId: 0,
    destinationId: 0
  });

  const getRows = useCallback((request: DataTableFilteredRequest) => diggingAjax.getTruckLoads(
    selectedLocation.diggingPatternId,
    selectedHourIndex,
    { ...request, unverifiedOnly }),
    [selectedLocation.diggingPatternId, selectedHourIndex, unverifiedOnly]
  );

  const {
    rows,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    sortField,
    sortDirection,
    setPage,
    appendRowSource,
    updateRowSource,
    removeRowSource,
    invalidate,
    tableMetaData,
  } = useDataTable<TruckLoadDTO, TruckLoadsTableMetaData, TruckLoadsPagedResult>({
    fetchFn: getRows,
    queryKey: ['chatree', 'truckLoads', selectedLocation.diggingPatternId, selectedHourIndex, unverifiedOnly],
    initialPage: 1,
    pageSize: 5,
    selectMeta: (result) => ({
      currentHourTotalLoads: result.currentHourTotalLoads,
      shiftTotalLoads: result.shiftTotalLoads
    })
  });

  const buildTruckLoadDtoFromTableRow = useCallback(
    (r: TableRow<number>, rowVersion?: string) => truckLoadFromTableRow(r, rowVersion, selectedLocation.diggingPatternId, selectedHourIndex),
    [selectedLocation.diggingPatternId, selectedHourIndex]
  );

  const handleInsertRow = useCallback(async () => {
    let isValid = true;
    if (insertState.truckId === 0) {
      enqueueSnackbar(t("trucksTable.truckRequired"), { variant: "error", autoHideDuration: 3000 });
      isValid = false;
    }
    if (insertState.loads == null || insertState.loads < 0) {
      enqueueSnackbar(t("trucksTable.loadsBelowZero"), { variant: "error", autoHideDuration: 3000 });
      isValid = false;
    }
    if (insertState.destinationId === 0) {
      enqueueSnackbar(t("trucksTable.destinationRequired"), { variant: "error", autoHideDuration: 3000 });
      isValid = false;
    }
    if (!insertState.materialTypeClassId) {
      enqueueSnackbar(t("trucksTable.materialTypeRequired"), { variant: "error", autoHideDuration: 3000 });
      isValid = false;
    }
    if (!isValid) return false;

    const payload: TruckLoadDTO = {
      loadingId: 0,
      diggingPatternId: selectedLocation.diggingPatternId,
      destinationId: insertState.destinationId,
      truckId: insertState.truckId,
      loads: insertState.loads,
      hour: selectedHourIndex,
      materialTypeClassId: insertState.materialTypeClassId
    };

    const response = await loadingsMutations.insertTruckLoad(payload);
    if (!response.success || !response.data || !response.data.truckLoad) {
      response.errors?.forEach((error, index) =>
        enqueueSnackbar(t("trucksTable.error", { message: error.description }), { variant: "error", autoHideDuration: 5000 + index * 1500 })
      );
      return false;
    }

    setInsertState(prev => ({
      truckId: prev.truckId,
      loads: 0,
      materialTypeClassId: prev.materialTypeClassId,
      destinationId: prev.destinationId
    }));
    enqueueSnackbar(t("trucksTable.loadAdded"), { variant: "success", autoHideDuration: 3000 });
    appendRowSource(response.data!.truckLoad!, {
      currentHourTotalLoads: response.data!.currentHourTotalLoads,
      shiftTotalLoads: response.data!.shiftTotalLoads,
    });

    return true;
  }, [insertState.truckId, insertState.loads, insertState.destinationId, insertState.materialTypeClassId, selectedLocation.diggingPatternId, selectedHourIndex, enqueueSnackbar, appendRowSource, t]);

  // Note: the counterpart to the Needs Checking filter. Editing a row was the only way to clear its flag,
  // so a load that was already correct could not be confirmed at all - you had to nudge a value and put it
  // back. Fine for the handful a person types, useless against telemetry, which produces hundreds.
  const handleVerifyAll = useCallback(async () => {
    if (!isOnlineNow()) {
      enqueueSnackbar(t("trucksTable.verifyAllOffline"), { variant: "warning", autoHideDuration: 4000 });
      return false;
    }

    const resp = await diggingAjax.verifyTruckLoads(
      selectedLocation.diggingPatternId,
      selectedHourIndex
    );
    if (!resp.success) {
      resp.errors?.forEach((e, i) =>
        enqueueSnackbar(e.description, { variant: "error", autoHideDuration: 4000 + i * 1000 })
      );
      return false;
    }

    const verifiedCount = resp.data ?? 0;
    enqueueSnackbar(
      verifiedCount === 0
        ? t("trucksTable.nothingToVerify")
        : t("trucksTable.loadsVerified", { count: verifiedCount }),
      { variant: "success", autoHideDuration: 3000 }
    );
    invalidate();
    return true;
  }, [selectedLocation.diggingPatternId, selectedHourIndex, enqueueSnackbar, invalidate, t]);

  // Note: previous is the row before the edit, forwarded so the Production list's cached loads move by
  // the difference rather than being missed.
  const handleUpdateRow = useCallback(
    async (row: TruckLoadDTO, previous?: TruckLoadDTO) => {
      let isValid = true;
      if (row.truckId === 0) {
        isValid = false;
        enqueueSnackbar(t("trucksTable.truckRequired"), { variant: "error", autoHideDuration: 3000 });
      }
      if (!row.materialTypeClassId) {
        isValid = false;
        enqueueSnackbar(t("trucksTable.materialTypeRequired"), { variant: "error", autoHideDuration: 3000 });
      }
      if (row.destinationId === 0) {
        isValid = false;
        enqueueSnackbar(t("trucksTable.destinationRequired"), { variant: "error", autoHideDuration: 3000 });
      }
      if (row.loads == null || row.loads < 0) {
        isValid = false;
        enqueueSnackbar(t("trucksTable.loadsBelowZeroOnEdit"), { variant: "error", autoHideDuration: 3000 });
      }
      if (!isValid) return false;

      const resp = await loadingsMutations.updateTruckLoad(row, previous);
      if (!resp.success || !resp.data || !resp.data.truckLoad) {
        resp.errors?.forEach((e, i) =>
          enqueueSnackbar(e.description, { variant: "error", autoHideDuration: 4000 + i * 1000 })
        );

        invalidate();
        return false;
      }

      enqueueSnackbar(t("trucksTable.loadUpdated"), { variant: "success", autoHideDuration: 1500 });
      const updated = resp.data!.truckLoad!;
      updateRowSource(r => r.loadingId === updated.loadingId, updated, {
        currentHourTotalLoads: resp.data!.currentHourTotalLoads,
        shiftTotalLoads: resp.data!.shiftTotalLoads,
      });

      return true;
    },
    [updateRowSource, enqueueSnackbar, invalidate, t]
  );

  const autoSaveDisplayedRow = useCallback(async (r: TableRow<number>, rowVersion?: string) => {
    try {
      return await handleUpdateRow(buildTruckLoadDtoFromTableRow(r, rowVersion));
    } catch (error) {
      console.error('Saving a truck load failed', error);
      enqueueSnackbar(t("trucksTable.loadSaveFailed"), { variant: "error", autoHideDuration: 5000 });
      return false;
    }
  }, [buildTruckLoadDtoFromTableRow, enqueueSnackbar, handleUpdateRow, t]);

  const handleDeleteRow = useCallback(
    async (loadingId: number, rowVersion?: string, removed?: TruckLoadDTO) => {
      const response = await loadingsMutations.deleteTruckLoad(loadingId, rowVersion, removed);
      if (!response.success || !response.data) {
        response.errors?.forEach((error, index) =>
          enqueueSnackbar(error.description, { variant: "error", autoHideDuration: 5000 + index * 1500 })
        );
        return false;
      }

      removeRowSource(r => r.loadingId === loadingId, {
        currentHourTotalLoads: response.data!.currentHourTotalLoads,
        shiftTotalLoads: response.data!.shiftTotalLoads,
      });
      enqueueSnackbar(t("trucksTable.loadDeleted"), { variant: "success", autoHideDuration: 3000 });
      return true;
    },
    [removeRowSource, enqueueSnackbar, t]
  );

  const translateRows = useCallback((row: TruckLoadDTO): TableRow => {
    // Note: a load for a truck or destination outside the caller's zones is shown by name and left alone;
    // the server refuses the write anyway, so this only keeps the controls honest.
    const locked = isReadOnly || row.canEdit === false;
    return {
      id: row.loadingId,
      values: [
        {
          name: "Truck",
          editable: false,
          sortable: true,
          type: "integer",
          tailwindClasses: "basis-[18%]",
          value: row.truckId,
          tabLockKey: "truck",
          render: (_tableValue, tableRow, helpers) => row.canEdit === false ? (
            <span className="text-[0.9rem] text-theme-text">{row.truckRef}</span>
          ) : (
            <ShowAllAutocomplete
              labels={TRUCK_LABELS}
              disabled={locked}
              value={truckOptions.find(t => t.plantId === helpers.draft.get()) ?? null}
              classes={{ root: "w-full" }}
              options={truckOptions}
              autoHighlight
              onChange={async (_, opt) => {
                const newValue = opt?.plantId ?? 0;
                helpers.draft.set(newValue);
                const updatedRow = withCellValue(tableRow, "Truck", "integer", newValue);
                await autoSaveDisplayedRow(updatedRow, row.rowVersion);
              }}
              getOptionLabel={o => o.plantRef}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder={t("trucksTable.selectTruck")}
                  size="small"
                  slotProps={{
                    ...params.slotProps,
                    input: {
                      ...params.slotProps.input,
                      inputRef: helpers.tabLock.registerInputRef,
                      onKeyDown: helpers.tabLock.handleLockedTabbing,
                      endAdornment: (
                        <>
                          {isTruckOptionsLoading ?
                            <FontAwesomeIcon icon={faSpinner} spin /> : null}
                          {params.slotProps.input.endAdornment}
                        </>
                      )
                    }
                  }}
                />
              )}
            />
          )
        },
        {
          name: "Loads",
          editable: true,
          sortable: true,
          type: "integer",
          tailwindClasses: "basis-[10%] py-1",
          value: row.loads,
          tabLockKey: "loads",
          render: (value, r, helpers) => (
            <MathInput
              disabled={locked}
              value={helpers.draft.get() ?? 0}
              step={1}
              inputRef={helpers.tabLock.registerInputRef}
              onKeyDown={helpers.tabLock.handleLockedTabbing}
              onValueChange={(val) => {
                if (val == null || Number.isNaN(val)) return;
                helpers.draft.set(val);
              }}
              onBlur={async (finalValue) => {
                if (finalValue == null || Number.isNaN(finalValue) || finalValue === value.value) return;

                const updatedRow = withCellValue(r, "Loads", "integer", finalValue);

                await autoSaveDisplayedRow(updatedRow, row.rowVersion);
              }}
            />
          ),
        },
        {
          name: "Material Type Class",
          editable: true,
          sortable: true,
          type: "integer",
          tailwindClasses: "basis-[15%] py-1",
          value: row.materialTypeClassId,
          tabLockKey: "material",
          render: (_value, r, helpers) => (
            <GroupedSelect
              disabled={locked}
              options={materialTypes}
              value={helpers.draft.get()}
              onChange={async newValue => {
                helpers.draft.set(newValue);

                const updatedRow = withCellValue(r, "Material Type Class", "integer", newValue);

                await autoSaveDisplayedRow(updatedRow, row.rowVersion);
              }}
              placeholder={t("trucksTable.selectMaterial")}
              loading={isMaterialTypesLoading}
              inputRef={helpers.tabLock.registerInputRef}
              onKeyDown={helpers.tabLock.handleLockedTabbing}
            />
          )
        },
        {
          name: "Destination",
          editable: true,
          sortable: true,
          type: "integer",
          tailwindClasses: "basis-[18%]",
          value: row.destinationId,
          tabLockKey: "destination",
          render: (_value, r, helpers) => {
            if (row.canEdit === false)
              return <span className="text-[0.9rem] text-theme-text">{row.destinationName}</span>;
            const materialClassID = cellValue(r, "Material Type Class", "integer") ?? row.materialTypeClassId;
            const isWaste = materialTypes.find(m => m.value === materialClassID)?.isWaste;
            const opts = destinationOptions.filter(d => (isWaste ? allowedWasteLocationTypeIds : allowedNonWasteLocationTypeIds).includes(d.locationTypeId));

            return (
              <Autocomplete
                disabled={locked}
                value={opts.find(o => o.id === helpers.draft.get()) ?? null}
                classes={{ root: "w-full" }}
                options={opts}
                loading={isDestinationOptionsDataLoading}
                getOptionLabel={(o) => o.name}
                onChange={async (_, opt) => {
                  const newValue = opt?.id ?? 0;
                  helpers.draft.set(newValue);

                  const updatedRow = withCellValue(r, "Destination", "integer", newValue);

                  await autoSaveDisplayedRow(updatedRow, row.rowVersion);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder={t("trucksTable.selectDestination")}
                    size="small"
                    slotProps={{
                      ...params.slotProps,
                      input: {
                        ...params.slotProps.input,
                        inputRef: helpers.tabLock.registerInputRef,
                        onKeyDown: helpers.tabLock.handleLockedTabbing,
                        endAdornment: <>{params.slotProps.input.endAdornment}</>
                      }
                    }}
                  />
                )}
              />
            );
          }
        },
        {
          name: "Added By",
          editable: false,
          sortable: true,
          type: "string",
          tailwindClasses: "basis-[11%]",
          value: row.addedBy
        },
        ...(telemetryEnabled ? [{
          name: "Source",
          editable: false,
          type: "custom" as const,
          tailwindClasses: "basis-[8%]",
          value: row.isVerified === false ? "Needs checking" : "",
          render: () => (
            <div className="flex items-center justify-center gap-1.5 w-full">
              <div className="text-theme-text-muted text-xs">
                {detectionSourceLabel(row.detectionSource)}
              </div>
              {/* Note: read-only here. There is no per-row accept endpoint on this screen - the trucks
                  table clears a whole hour at once and Stoppages clears by editing - so the mark reports
                  state without pretending to be a control. */}
              {isAutoDetected(row.detectionSource) && (
                <VerificationIndicator isVerified={row.isVerified !== false} subject={t("trucksTable.verificationSubject")} />
              )}
            </div>
          )
        }] : []),
        {
          name: "Actions",
          type: "custom",
          tailwindClasses: "basis-[20%]",
          value: "custom",
          render: () => (
            <TruckLoadActions
              disabled={locked}
              onDelete={() => handleDeleteRow(row.loadingId, row.rowVersion)}
              onMove={() => {
                const otherLocations = availableLocations.filter(
                  loc => loc.diggingPatternId !== selectedLocation.diggingPatternId
                );

                showModal({
                  content: () => (
                    <MoveLoadingRecordModal
                      availableLocations={otherLocations}
                      currentLocation={selectedLocation}
                      onMove={async (newDiggingPatternId) => {
                        const success = await handleMoveLoadingRecord(row.loadingId, newDiggingPatternId);
                        if (success) {
                          invalidate();
                          enqueueSnackbar(t("trucksTable.loadMoved"), { variant: "success", autoHideDuration: 3000 });
                        }
                        return success;
                      }}
                    />
                  )
                });
              }}
            />
          )
        }
      ]
    };
  }, [
    isReadOnly,
    truckOptions,
    autoSaveDisplayedRow,
    isTruckOptionsLoading,
    materialTypes,
    isMaterialTypesLoading,
    destinationOptions,
    isDestinationOptionsDataLoading,
    allowedWasteLocationTypeIds,
    allowedNonWasteLocationTypeIds,
    availableLocations,
    showModal,
    selectedLocation,
    handleMoveLoadingRecord,
    enqueueSnackbar,
    telemetryEnabled,
    invalidate,
    handleDeleteRow,
    t
  ])

  const displayData = useMemo(() => rows.map(translateRows), [rows, translateRows]);

  const { pulseRowKey } = useScrollAndPulseRow(
    initialLoadingId != null ? String(initialLoadingId) : null,
    !isLoading,
    useCallback(() => onNavigationConsumed?.(), [onNavigationConsumed]),
    { scrollDelayMs: 0, pulseDelayMs: 500, pulseDurationMs: 2800, expireMs: 500 + 2800 }
  );

  useEffect(() => {
    if (navConsumedRef.current) return;

    const hasHighlight = initialLoadingId != null;
    const hasHour = initialHourIndex != null;

    if (!hasHour || hasHighlight) return;
    if (isLoading) return;
    if (selectedHourIndex !== initialHourIndex) return;

    navConsumedRef.current = true;
    onNavigationConsumed?.();
  }, [initialHourIndex, initialLoadingId, selectedHourIndex, isLoading, onNavigationConsumed]);

  useEffect(() => {
    registerUpdateTrucksTableRowFn(() => invalidate());

    return () => registerUpdateTrucksTableRowFn(() => { });
  }, [invalidate, registerUpdateTrucksTableRowFn]);

  const offlineShiftTotal = useOfflineShiftTotal(selectedLocation.diggingPatternId, isOnline);

  const currentHourLabel = useMemo(() => {
    const start = clockHourFromIndex(selectedHourIndex);
    const end = (start + 1) % 24;
    return `${start}-${end}`;
  }, [selectedHourIndex, clockHourFromIndex]);

  return (
    <div className="flex flex-col w-full bg-theme-surface-2 border border-theme-table-border p-4 text-theme-text">
      <h2 className="text-2xl font-bold mb-4">
        <Trans t={t} i18nKey="trucksTable.title" values={{ blastMasterId: selectedLocation.blastMasterId ?? selectedLocation.patternBlastId }} components={{ underline: <span className="underline" /> }} />
      </h2>

      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center space-x-2 overflow-auto fade-up">
          {hours.map(h => {
            const isLive = h.index === liveHourIndex;
            const isFuture = isHourSlotInFuture(h.index, liveHourIndex ?? -1);
            return (
              <button
                key={h.index}
                disabled={isFuture}
                title={isFuture ? t("trucksTable.hourNotStarted") : isLive ? t("trucksTable.currentHour") : undefined}
                onClick={() => setSelectedHourIndex(h.index)}
                className={`flex items-center gap-1.5 px-2 py-1 rounded border border-theme-info whitespace-nowrap cursor-pointer transition disabled:opacity-40 disabled:cursor-not-allowed ${h.index === selectedHourIndex ? "bg-theme-surface hover:bg-theme-surface-2 text-theme-info" : "bg-theme-info hover:bg-theme-info-hover text-white"
                  }`}
              >
                {isLive && (
                  <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
                    <span className="absolute inline-flex h-full w-full rounded-full bg-theme-success opacity-75 animate-slow-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-theme-success" />
                  </span>
                )}
                {h.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-4 shrink-0">
          {telemetryEnabled && <>
            <label className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
              <input
                type="checkbox"
                checked={unverifiedOnly}
                className="w-6 h-6 border-theme-border rounded-md focus:ring-blue-500 focus:ring-2"
                title={t("trucksTable.needsCheckingHint")}
                onChange={(e) => setUnverifiedOnly(e.target.checked)}
              />
              {t("trucksTable.needsChecking")}
            </label>

            {canVerify && <SaveButton
              buttonLabel={t("trucksTable.verifyAll")}
              confirmMessage={t("trucksTable.verifyAllConfirm")}
              disabled={isReadOnly || !isOnline}
              handleSave={handleVerifyAll}
            />}
          </>}

          <CopyPreviousHourButton
            disabled={isReadOnly || !isOnline}
            onCopy={async () => {
              if (!isOnlineNow()) {
                enqueueSnackbar(t("trucksTable.copyOffline"), { variant: "warning", autoHideDuration: 5000 });
                return false;
              }
              const newTruckLoads = await diggingAjax.copyPreviousHourLoads(
                selectedLocation.diggingPatternId,
                selectedHourIndex
              );

              if (!newTruckLoads.success || !newTruckLoads.data) {
                newTruckLoads.errors?.forEach((error, index) =>
                  enqueueSnackbar(error.description, { variant: "error", autoHideDuration: 5000 + index * 1500 })
                );
                return false;
              }

              if (newTruckLoads.data.length === 0) {
                enqueueSnackbar(t("trucksTable.nothingToCopy"), { variant: "success", autoHideDuration: 3000 });
                return true;
              }

              enqueueSnackbar(t("trucksTable.loadsCopied"), { variant: "success", autoHideDuration: 3000 });
              newTruckLoads.data.forEach(l => appendRowSource(l));

              invalidate();
              return true;
            }}
          />
        </div>
      </div>

      <DataTable
        data={displayData}
        isLoading={isLoading}
        isBackgroundFetching={isBackgroundFetching}
        pulseRowKey={pulseRowKey}
        error={isOnline ? error : null}
        pageNumber={pageNumber}
        setPage={setPage}
        sortField={sortField}
        sortDirection={sortDirection}
        minTableWidthPixels={1320}
        minTableHeightCssValue="320px"
        maxTableHeightCssValue="360px"
        disableContainer={true}
        enableTabLocking
        lockedTabKey={lockedTabKey}
        setLockedTabKey={setLockedTabKey}
        noResultsContent={<TrucksTableNoResultsContent />}
        tableFooterContent={<TrucksTableFooter
          shiftTotal={isOnline ? tableMetaData?.shiftTotalLoads : offlineShiftTotal}
          currentHourLabel={currentHourLabel}
          // Note: while the Needs Checking filter is on the visible rows are only part of the hour, so
          // summing them would understate it. The server's figure counts every load either way.
          currentHourSum={unverifiedOnly
            ? tableMetaData?.currentHourTotalLoads ?? 0
            : rows.reduce((sum, load) => sum + load.loads, 0)}
          isLoading={isLoading}
        />}
        insertRowRenderer={() => (
          <InsertInputsRow
            isReadOnly={isReadOnly}
            state={insertState}
            setState={setInsertState}
            onInsert={handleInsertRow}
            truckOptions={truckOptions}
            isTruckOptionsLoading={isTruckOptionsLoading}
            materialTypes={materialTypes}
            isMaterialTypesLoading={isMaterialTypesLoading}
            destinationOptions={destinationOptions}
            allowedNonWasteLocationTypeIds={allowedNonWasteLocationTypeIds}
            allowedWasteLocationTypeIds={allowedWasteLocationTypeIds}
            isDestinationOptionsDataLoading={isDestinationOptionsDataLoading}
          />
        )}
        overrideHeaderCells={[
          { name: "Truck", label: t("fields.truck"), tailwindClasses: "basis-[18%]", tabLockKey: "truck" },
          { name: "Loads", label: t("fields.loads"), tailwindClasses: "basis-[10%]", tabLockKey: "loads" },
          { name: "Material type", label: t("columns.materialType"), tailwindClasses: "basis-[15%]", tabLockKey: "material" },
          { name: "Destination", label: t("fields.destination"), tailwindClasses: "basis-[18%]", tabLockKey: "destination" },
          { name: "Added by", label: t("columns.addedBy"), tailwindClasses: "basis-[11%]" },
          ...(telemetryEnabled ? [{ name: "Source", label: t("fields.source"), tailwindClasses: "basis-[8%]" }] : []),
          { name: "Actions", label: t("fields.actions"), tailwindClasses: "basis-[20%]" }
        ]}
      />
    </div>
  );
};
